import type { CoverLayout, ReportTemplate } from '../types/reportTemplate';
import type { ReportTheme } from '../types/reportTheme';
import type { EffectiveTemplateConfig } from './effectiveTemplateConfig';
import { normalizeCoverLayout } from './normalizeCoverLayout';
import { getThemePreset } from './reportThemePresets';
import { isEditorialLayoutsEnabled } from './runtimeFlags';
import { STOCK_DEFAULT_COVER_LAYOUT } from './stockDefaultTemplate';

/**
 * Part L2 — the editorial-masthead title page is the DEFAULT cover for every
 * report type when the VITE_EDITORIAL_LAYOUTS flag is ON. It replaces ONLY the
 * "default floor" rungs of the precedence — the preset's own default cover and
 * the legacy `theme.coverLayout` base — never an EXPLICIT user/template/report
 * cover choice (those still win unchanged). With the flag OFF this helper is a
 * no-op (returns the caller's value), so cover resolution is byte-identical to
 * today across every preset and the 66-cell visual matrix.
 */
const EDITORIAL_DEFAULT_COVER_LAYOUT: CoverLayout = 'editorial-masthead';
function editorialDefaultCover(fallback: CoverLayout): CoverLayout {
  return isEditorialLayoutsEnabled() ? EDITORIAL_DEFAULT_COVER_LAYOUT : fallback;
}

/**
 * The TERMINAL cover layout for a report that expressed NO cover choice
 * anywhere — no linked/resolved template, no preset, no explicit
 * `report.coverLayout` / `coverLayoutAtGen` snapshot. This is the curated
 * STOCK default (Institutional · Masthead), matching
 * {@link import('./stockDefaultTemplate').STOCK_DEFAULT_TEMPLATE} which
 * `resolveActiveReportTemplate` substitutes as the terminal template fallback.
 * Keeping the two in lock-step means a no-choice report resolves to the SAME
 * Masthead cover whether the stock template object is threaded (the live
 * viewer / print-export path) or absent (a primitive caller that passes a
 * `null` template).
 *
 * J2 (#stock-default-cover): previously this terminal was `HERO_COVER_LAYOUT`,
 * so a report with NO template AND NO explicit cover resolved to Hero — and on
 * the templateless path the legacy `DEFAULT_THEME.coverLayout === 'hero-image'`
 * base FLOOR (NOT a user choice) masqueraded as a choice and beat the stock
 * Masthead. The terminal is now the stock cover, so a no-choice report renders
 * the curated Masthead title page (journal title + abstract) instead of the
 * logo / address / exec-summary / boundary-thumbnail Hero cover.
 *
 * An EXPLICIT cover choice still WINS unchanged: a real template/preset cover
 * (`fromTemplate`), or a stored `report.coverLayout` / `coverLayoutAtGen` the
 * user or an assigned template set, is honoured exactly as before — ONLY the
 * absent/default case changes.
 */
const NO_CHOICE_COVER_LAYOUT: CoverLayout = STOCK_DEFAULT_COVER_LAYOUT;

/**
 * Resolve which cover variant to render.
 *
 * Template-linked reports always follow the active template's
 * `coverLayout` / theme merge — never a gen-time `report.coverLayout`
 * snapshot left over from an earlier template version.
 */
export function resolveCoverLayout(
  report: { templateId?: string; coverLayout?: CoverLayout; coverLayoutAtGen?: CoverLayout },
  template: ReportTemplate | null | undefined,
  templateConfig: EffectiveTemplateConfig | null | undefined,
  theme: ReportTheme,
  usesLiveTemplateLayout: boolean,
): CoverLayout {
  // SINGLE THEME SEAM — fold the active template's PRESET cover default into the
  // precedence. Previously this resolver only consulted the explicit
  // template/templateConfig `coverLayout` and `theme.coverLayout`, so an
  // assigned preset (e.g. journal ⇒ `classic`) was IGNORED here while the
  // theme-merge path (resolveEffectiveTheme) honoured it — the body folded the
  // preset cover but this resolver fell through to the default hero, splitting
  // the cover off the body theme. Precedence: explicit template/templateConfig
  // choice (user override) wins, THEN the preset default, THEN `theme.coverLayout`.
  // Part L2 — the preset default cover and the legacy `theme.coverLayout` base
  // are the "default floor" rungs: when editorial layouts are ON, they yield to
  // the editorial-masthead title page (the editorial STRUCTURE includes the
  // editorial cover; the chosen preset still supplies the PALETTE). An EXPLICIT
  // template/templateConfig cover choice (the user picked a cover in Studio)
  // still wins above this floor. Flag OFF ⇒ `editorialDefaultCover` is identity,
  // so this is byte-stable.
  const presetLayout = getThemePreset(template?.themePreset)?.coverLayout;
  const fromTemplate =
    template?.coverLayout
    ?? templateConfig?.coverLayout
    ?? editorialDefaultCover(presetLayout ?? theme.coverLayout);

  const linkedId = report.templateId?.trim();
  const templateId = template?.id?.trim();

  // Template-linked reports: a LIVE template wins (never resurrect a stale
  // gen-time snapshot). But when the linked template row could NOT be
  // resolved this session (still hydrating / private / cross-org —
  // `template == null`), fall back to the report's gen-time cover snapshot
  // BEFORE the no-choice default, so an assigned Masthead/Classic is not lost.
  if (linkedId) {
    if (templateId && linkedId === templateId) {
      return normalizeCoverLayout(fromTemplate ?? NO_CHOICE_COVER_LAYOUT);
    }
    if (template) {
      return normalizeCoverLayout(fromTemplate ?? NO_CHOICE_COVER_LAYOUT);
    }
    // No live template — prefer the durable gen-time snapshot, then the
    // live (un-stripped) report cover, then the theme/preset default.
    const genTimeSnapshot = report.coverLayoutAtGen ?? report.coverLayout;
    return normalizeCoverLayout(
      genTimeSnapshot
        ?? fromTemplate
        ?? editorialDefaultCover(theme.coverLayout ?? NO_CHOICE_COVER_LAYOUT),
    );
  }

  const linked = !!linkedId && !!templateId && linkedId === templateId;
  /** Report points at a missing/deleted template row but a fallback template was resolved. */
  const linkBroken = !!linkedId && !!templateId && linkedId !== templateId;
  const followTemplate =
    usesLiveTemplateLayout
    || linked
    || linkBroken
    || (!!template && !linkedId);

  if (followTemplate) {
    return normalizeCoverLayout(fromTemplate ?? NO_CHOICE_COVER_LAYOUT);
  }

  // Truly templateless terminal — reached ONLY when `template` is null/absent
  // (else `followTemplate` is true via `!!template && !linkedId`) AND the
  // report carries no linked template id. Here `fromTemplate` collapses to the
  // base `theme.coverLayout`, which on this path is just the legacy
  // `DEFAULT_THEME` FLOOR (`hero-image`) — NOT a user/template choice — so it
  // must NOT be treated as a cover selection. Honour an EXPLICIT
  // `report.coverLayout` snapshot the user set; otherwise fall to the curated
  // stock Masthead default (J2). Previously this consulted
  // `fromTemplate`/`theme.coverLayout` and so resolved to Hero for a no-choice
  // report.
  return normalizeCoverLayout(
    report.coverLayout ?? editorialDefaultCover(NO_CHOICE_COVER_LAYOUT),
  );
}
